import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  Observable,
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { CurrentUserService } from '../auth/current-user.service';
import { TokenService } from '../auth/token.service';
import { ApiResponse } from '../models/api-response.models';
import { RefreshTokenRequest } from '../models/auth.models';
import { ApiResponseError, isApiResponse, unwrapApiResponse } from '../utils/api-response';

interface RefreshedTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

let refreshRequest$: Observable<string> | null = null;

export const errorInterceptor: HttpInterceptorFn = (request, next) => {
  const tokens = inject(TokenService);
  const currentUser = inject(CurrentUserService);
  const router = inject(Router);
  const rawHttp = new HttpClient(inject(HttpBackend));

  return next(request).pipe(
    map((event) => {
      if (event instanceof HttpResponse && isApiResponse(event.body) && !event.body.success) {
        throw new ApiResponseError(event.body as ApiResponse<unknown>);
      }
      return event;
    }),
    catchError((error: unknown) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        isRefreshableRequest(request.url) &&
        tokens.getRefreshToken()
      ) {
        return refreshAccessToken(rawHttp, tokens).pipe(
          catchError((refreshError: unknown) => {
            clearSession(tokens, currentUser, router);
            return throwError(() => refreshError);
          }),
          switchMap((accessToken) =>
            next(request.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })),
          ),
        );
      }

      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        isSessionValidationRequest(request.url)
      ) {
        clearSession(tokens, currentUser, router);
      }

      return throwError(() => error);
    }),
  );
};

function refreshAccessToken(rawHttp: HttpClient, tokens: TokenService): Observable<string> {
  if (refreshRequest$) return refreshRequest$;

  const refreshToken = tokens.getRefreshToken();
  if (!refreshToken)
    return throwError(() => new Error('Your session has expired. Please sign in again.'));

  const payload: RefreshTokenRequest = {
    refreshToken,
    ipAddress: null,
    userAgent: navigator.userAgent,
  };

  refreshRequest$ = rawHttp
    .post<ApiResponse<RefreshedTokens> | RefreshedTokens>(
      `${environment.identityApiBaseUrl.replace(/\/$/, '')}/auth/refresh-token`,
      payload,
    )
    .pipe(
      map((response) => unwrapApiResponse(response)),
      tap((response) => tokens.setTokens(response.accessToken, response.refreshToken)),
      map((response) => response.accessToken),
      finalize(() => (refreshRequest$ = null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

  return refreshRequest$;
}

function isRefreshableRequest(url: string): boolean {
  const isApiRequest =
    url.startsWith(environment.identityApiBaseUrl) ||
    url.startsWith(environment.businessApiBaseUrl) ||
    url.startsWith(environment.marketplaceApiBaseUrl);

  return isApiRequest && !isAnonymousAuthRequest(url) && !url.includes('/auth/refresh-token');
}

function isAnonymousAuthRequest(url: string): boolean {
  return [
    '/auth/platform/login',
    '/auth/business/login',
    '/auth/forgot-password',
    '/auth/reset-password',
  ].some((path) => url.includes(path));
}

function clearSession(tokens: TokenService, currentUser: CurrentUserService, router: Router): void {
  tokens.clear();
  currentUser.setUser(null);
  void router.navigate([router.url.startsWith('/business') ? '/login' : '/admin-login']);
}

function isSessionValidationRequest(url: string): boolean {
  return url.includes('/auth/me') || url.includes('/auth/refresh-token');
}
